import React, { useEffect, useState } from "react";
import { CRow } from "@coreui/react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { UploadAgentsBooksellersPublishersCSV } from "api/mutations";
import { Modal } from "components/Modal";
import { Button } from "components/Button";
import { AgentBooksellerPublisherAll } from "api/queries";
import { RenderList } from "components/RenderList";
import { Spiner } from "components/Spiner";
import CIcon from "@coreui/icons-react";
import { UploadInput } from "components/UploadInput";
import {
  MultilineSearch,
  useMultilineSearch,
} from "../../hooks/useMultilineSearch";
import { AllPublishers } from "api/mobileQueries";

const AgentBooksellerPublisherPage = React.memo((props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const limitPerPage = 50;

  const [pageCount, setPageCount] = useState(1);
  // const [keyForFilterQuery, setKeyForFilterQuery] = useState('')
  const [filterQueries, setFilterQueries] = useState([]);
  const [visibleUploadModal, setVisibleUploadModal] = useState(false);
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState();
  const [showError, setShowError] = useState(false);
  const setActivePage = (page) => setPageCount(page);

  const uploadCSV = useMutation(UploadAgentsBooksellersPublishersCSV);
  const queryFunc = () =>
    AgentBooksellerPublisherAll(pageCount || 1, limitPerPage, filterQueries);

  /**
   * Query with pagination @staleTime: period in save data
   */
  const AgentBooksellerPublisherList = useQuery(
    ["AgentBooksellerPublisherAll", pageCount, limitPerPage, filterQueries],
    queryFunc,
    {
      retry: false,
      keepPreviousData: true,
      staleTime: 60000,
    }
  );

  const allPublishers = useQuery(["AllPublishers"], AllPublishers, {
    retry: false,
    keepPreviousData: true,
    staleTime: 60000,
  });

  const refreshQuery = () =>
    queryClient.removeQueries("AgentBooksellerPublisherAll", {
      inactive: true,
    });

  useEffect(() => {
    return refreshQuery;
  }, []);

  const handleVisibleUploadModal = () => {
    setShowError(false);
    setFile(null);
    setFileName("");
    setVisibleUploadModal(!visibleUploadModal);
  };

  const handleCloseUploadModal = () => {
    const close = new Promise((resolve) => {
      resolve(setVisibleUploadModal(false));
    });
    close.then(() =>
      setTimeout(() => {
        uploadCSV.reset();
        queryClient.resetQueries("AgentBooksellerPublisherAll");
      }, 500)
    );
  };

  const uploadSubmit = async () => {
    if (file) {
      const form = new window.FormData();
      form.set("file", file);
      await uploadCSV.mutateAsync(form);
    } else {
      setShowError(true);
    }
  };

  const handleUpload = (e) => {
    const name = e.target.files[0].name.split(".")[0];
    setFileName(name);
    setShowError(false);
    setFile(e.target.files[0]);
  };

  const header = [
    { key: "agentSurname", label: t("Agent surname") },
    "booksellerCompanyName",
    "booksellerProvince",
    "booksellerCode",
    "publisherCompanyName",
    "publisherCode",
  ];
  const searchFieldNames = [
    "agentSurname",
    "booksellerCompanyName",
    "booksellerProvince",
    "booksellerCode",
    "publisherCompanyName",
    "publisherCode",
  ];
  const searchCustomFields = [
    {
      id: "publisherCompanyName",
      type: "select",
      options: allPublishers.data
        ? allPublishers.data.data.map(({ value }) => ({ label: value, value }))
        : [],
    },
  ];
  const { searchProps } = useMultilineSearch(
    searchFieldNames,
    setFilterQueries,
    searchCustomFields
  );

  return (
    <>
      <Modal
        visible={visibleUploadModal}
        setVisible={handleCloseUploadModal}
        title={uploadCSV.isSuccess ? t("success") : t("Upload file")}
        type={uploadCSV.isSuccess ? "success" : "primary"}
        fetching={uploadCSV.isLoading}
        body={
          uploadCSV.isSuccess ? (
            t("Agent objectives data changed successfully")
          ) : (
            <UploadInput
              onChange={handleUpload}
              fileName={fileName}
              error={showError}
              errorText={t("Please choose file")}
            />
          )
        }
        toDoBtn={uploadCSV.isSuccess ? t("Close") : t("Submit")}
        toDoFunc={!uploadCSV.isSuccess ? uploadSubmit : handleCloseUploadModal}
        cancelBtn={t("cancel")}
      />
      <CRow className="justify-content-end mt-3 pr-4 pl-4">
        <MultilineSearch
          className="col-sm-10 col-md-10 col-lg-10"
          {...searchProps}
        />
        <Button
          variant="outline"
          wrapped
          size="md"
          color="primary"
          title={t("Upload")}
          onClick={handleVisibleUploadModal}
          icon={<CIcon name="cil-cloud-upload" />}
        />
      </CRow>
      <CRow className="justify-content-center pr-4 pl-4">
        {AgentBooksellerPublisherList.isFetching ? (
          <Spiner />
        ) : (
          AgentBooksellerPublisherList.isSuccess && (
            <RenderList
              header={t("Agent objectives")}
              data={AgentBooksellerPublisherList.data.data.results}
              fields={header}
              currentPage={pageCount}
              limit={AgentBooksellerPublisherList.data.data.limit}
              setActivePage={setActivePage}
              totalPages={AgentBooksellerPublisherList.data.data.totalPages}
              scopedSlots={{
                agentSurname: (item) => (
                  <td>{item.agent && <>{item.agent.surname}</>}</td>
                ),
              }}
            />
          )
        )}
      </CRow>
    </>
  );
});

export { AgentBooksellerPublisherPage };
